import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Grid,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  useTheme,
  alpha,
  Autocomplete
} from '@mui/material';
import {
  LocalPharmacy as MedicationIcon,
  Image as ImagingIcon,
  Science as LabIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Send as SendIcon
} from '@mui/icons-material';

// Mock data for medications, imaging studies, and lab tests
const MEDICATIONS = [
  'Acetaminophen', 'Ibuprofen', 'Amoxicillin', 'Lisinopril', 'Atorvastatin',
  'Metformin', 'Levothyroxine', 'Amlodipine', 'Omeprazole', 'Albuterol',
  'Sertraline', 'Gabapentin', 'Hydrochlorothiazide', 'Metoprolol', 'Losartan'
];

const IMAGING_STUDIES = [
  'X-ray', 'CT Scan', 'MRI', 'Ultrasound', 'PET Scan',
  'Mammogram', 'Bone Density Scan', 'Echocardiogram', 'Angiogram'
];

const LAB_TESTS = [
  'Complete Blood Count (CBC)', 'Basic Metabolic Panel', 'Comprehensive Metabolic Panel',
  'Lipid Panel', 'Thyroid Function Tests', 'Hemoglobin A1C', 'Urinalysis',
  'Liver Function Tests', 'Coagulation Panel', 'C-Reactive Protein',
  'Vitamin D Level', 'Iron Studies', 'B12 and Folate', 'Hormone Panel'
];

/**
 * PrescriptionForm Component
 * 
 * This component provides a form for doctors to prescribe medications, imaging studies, and lab tests.
 * 
 * @param {Object} props
 * @param {string} props.patientId - The ID of the patient
 * @param {string} props.consultationId - The ID of the consultation
 * @param {Function} props.onPrescriptionCreated - Callback function when a prescription is created
 */
const PrescriptionForm = ({ patientId, consultationId, onPrescriptionCreated }) => {
  const theme = useTheme();
  const [prescriptionType, setPrescriptionType] = useState('medication');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Medication state
  const [medication, setMedication] = useState('');
  const [dosage, setDosage] = useState('');
  const [frequency, setFrequency] = useState('');
  const [duration, setDuration] = useState('');
  const [medicationNotes, setMedicationNotes] = useState('');
  
  // Imaging state
  const [imagingType, setImagingType] = useState('');
  const [bodyPart, setBodyPart] = useState('');
  const [imagingNotes, setImagingNotes] = useState('');
  
  // Lab test state
  const [labTests, setLabTests] = useState([]);
  const [labNotes, setLabNotes] = useState('');

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);
    
    try {
      // Validate form based on prescription type
      if (prescriptionType === 'medication' && (!medication || !dosage || !frequency)) {
        throw new Error('Please fill in all required medication fields');
      } else if (prescriptionType === 'imaging' && (!imagingType || !bodyPart)) {
        throw new Error('Please fill in all required imaging fields');
      } else if (prescriptionType === 'lab' && labTests.length === 0) {
        throw new Error('Please select at least one lab test');
      }
      
      // Ensure we have at least a consultationId
      if (!consultationId) {
        throw new Error('Consultation ID is required to create a prescription');
      }
      
      // In a real implementation, this would be an API call to create the prescription
      // For now, we'll simulate it
      setTimeout(() => {
        // Create prescription object based on type
        let prescriptionData = {
          id: Date.now().toString(),
          patientId: patientId || consultationId, // Use consultationId as fallback
          consultationId,
          type: prescriptionType,
          createdAt: new Date().toISOString(),
          status: 'pending'
        };
        
        if (prescriptionType === 'medication') {
          prescriptionData = {
            ...prescriptionData,
            medication,
            dosage,
            frequency,
            duration,
            notes: medicationNotes
          };
        } else if (prescriptionType === 'imaging') {
          prescriptionData = {
            ...prescriptionData,
            imagingType,
            bodyPart,
            notes: imagingNotes
          };
        } else if (prescriptionType === 'lab') {
          prescriptionData = {
            ...prescriptionData,
            tests: labTests,
            notes: labNotes
          };
        }
        
        // Call the callback function with the new prescription
        if (onPrescriptionCreated) {
          onPrescriptionCreated(prescriptionData);
        }
        
        // Reset form
        resetForm();
        setSuccess(true);
        setLoading(false);
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setSuccess(false);
        }, 3000);
      }, 1000);
    } catch (err) {
      console.error('Error creating prescription:', err);
      setError(err.message || 'Failed to create prescription. Please try again.');
      setLoading(false);
    }
  };
  
  // Reset form based on prescription type
  const resetForm = () => {
    if (prescriptionType === 'medication') {
      setMedication('');
      setDosage('');
      setFrequency('');
      setDuration('');
      setMedicationNotes('');
    } else if (prescriptionType === 'imaging') {
      setImagingType('');
      setBodyPart('');
      setImagingNotes('');
    } else if (prescriptionType === 'lab') {
      setLabTests([]);
      setLabNotes('');
    }
  };
  
  // Handle prescription type change
  const handlePrescriptionTypeChange = (e) => {
    setPrescriptionType(e.target.value);
    resetForm();
    setError(null);
    setSuccess(false);
  };
  
  // Render form based on prescription type
  const renderForm = () => {
    switch (prescriptionType) {
      case 'medication':
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                options={MEDICATIONS}
                value={medication}
                onChange={(_, newValue) => setMedication(newValue)}
                freeSolo
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Medication"
                    required
                    fullWidth
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                label="Dosage"
                value={dosage}
                onChange={(e) => setDosage(e.target.value)}
                required
                fullWidth
                placeholder="e.g., 500mg"
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                label="Frequency"
                value={frequency}
                onChange={(e) => setFrequency(e.target.value)}
                required
                fullWidth
                placeholder="e.g., Twice daily"
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                label="Duration"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                fullWidth
                placeholder="e.g., 7 days"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Additional Instructions"
                value={medicationNotes}
                onChange={(e) => setMedicationNotes(e.target.value)}
                fullWidth
                multiline
                rows={2}
                placeholder="e.g., Take with food"
              />
            </Grid>
          </Grid>
        );
        
      case 'imaging':
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Imaging Type</InputLabel>
                <Select
                  value={imagingType}
                  onChange={(e) => setImagingType(e.target.value)}
                  label="Imaging Type"
                >
                  {IMAGING_STUDIES.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Body Part"
                value={bodyPart}
                onChange={(e) => setBodyPart(e.target.value)}
                required
                fullWidth
                placeholder="e.g., Chest, Left Knee"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Clinical Indication & Notes"
                value={imagingNotes}
                onChange={(e) => setImagingNotes(e.target.value)}
                fullWidth
                multiline
                rows={3}
                placeholder="e.g., Suspected fracture after fall"
              />
            </Grid>
          </Grid>
        );
        
      case 'lab':
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={LAB_TESTS}
                value={labTests}
                onChange={(_, newValue) => setLabTests(newValue)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option}
                      {...getTagProps({ index })}
                      color="primary"
                      variant="outlined"
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Lab Tests"
                    required
                    placeholder="Select lab tests"
                  />
                )}
              />
              <FormHelperText>Select one or more lab tests</FormHelperText>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Clinical Indication & Notes"
                value={labNotes}
                onChange={(e) => setLabNotes(e.target.value)}
                fullWidth
                multiline
                rows={3}
                placeholder="e.g., Monitoring diabetes control"
              />
            </Grid>
          </Grid>
        );
        
      default:
        return null;
    }
  };
  
  return (
    <Paper elevation={0} sx={{ p: 0, mb: 3 }}>
      <Box sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
          {prescriptionType === 'medication' && <MedicationIcon sx={{ mr: 1 }} />}
          {prescriptionType === 'imaging' && <ImagingIcon sx={{ mr: 1 }} />}
          {prescriptionType === 'lab' && <LabIcon sx={{ mr: 1 }} />}
          Create Prescription
        </Typography>
      </Box>
      
      <Box sx={{ p: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Prescription created successfully!
          </Alert>
        )}
        
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Prescription Type</InputLabel>
          <Select
            value={prescriptionType}
            onChange={handlePrescriptionTypeChange}
            label="Prescription Type"
          >
            <MenuItem value="medication">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <MedicationIcon sx={{ mr: 1 }} />
                Medication
              </Box>
            </MenuItem>
            <MenuItem value="imaging">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ImagingIcon sx={{ mr: 1 }} />
                Imaging Study
              </Box>
            </MenuItem>
            <MenuItem value="lab">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LabIcon sx={{ mr: 1 }} />
                Laboratory Tests
              </Box>
            </MenuItem>
          </Select>
        </FormControl>
        
        <form onSubmit={handleSubmit}>
          {renderForm()}
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button
              type="button"
              onClick={resetForm}
              sx={{ mr: 1 }}
              disabled={loading}
            >
              Reset
            </Button>
            
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Prescription'}
            </Button>
          </Box>
        </form>
      </Box>
    </Paper>
  );
};

export default PrescriptionForm; 